/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */

// Sync custom Rio option buttons with the theme combo-box selectors when both are present.
let isSyncingRioOptions = false;

const rioOptionsObserver = new MutationObserver(() => {
  const comboBoxOptions = document.querySelectorAll('.combo-box__option-item');
  if (!comboBoxOptions.length) {
    return;
  }

  comboBoxOptions.forEach((option) => {
    if (option.dataset.listenerAdded) {
      return;
    }

    option.dataset.listenerAdded = 'true';
    option.addEventListener('click', () => {
      if (isSyncingRioOptions) {
        return;
      }

      isSyncingRioOptions = true;
      const selectedText = option.textContent.trim();
      const target = document.querySelector(`.rio-options [data-option-value="${CSS.escape(selectedText)}"]`);

      if (target) {
        target.click();
        const stickyValue = document.querySelector('.product-sticky-form__form .select__selected-value');
        if (stickyValue) {
          stickyValue.textContent = selectedText;
        }
      }

      setTimeout(() => {
        isSyncingRioOptions = false;
      }, 50);
    });
  });

  document.querySelectorAll('.rio-options [data-option-value]').forEach((button) => {
    if (button.dataset.listenerAdded) {
      return;
    }

    button.dataset.listenerAdded = 'true';
    button.addEventListener('click', () => {
      if (isSyncingRioOptions) {
        return;
      }

      isSyncingRioOptions = true;
      const selectedText = button.dataset.optionValue.trim();
      const comboOption = Array.from(document.querySelectorAll('.combo-box__option-item')).find(
        (option) => option.textContent.trim() === selectedText
      );

      if (comboOption) {
        comboOption.click();
        const stickyValue = document.querySelector('.product-sticky-form__form .select__selected-value');
        if (stickyValue) {
          stickyValue.textContent = selectedText;
        }
      }

      setTimeout(() => {
        isSyncingRioOptions = false;
      }, 50);
    });
  });
});

rioOptionsObserver.observe(document.body, {
  childList: true,
  subtree: true
});

// Keep LensAdvizor UI outside the stock payment container so it survives variant rerenders.
(function() {
  const PRODUCT_FORM_SELECTOR = 'form[is="product-form"]';
  const BUY_BUTTONS_SELECTOR = '[data-block-type="buy-buttons"]';
  const PAYMENT_CONTAINER_SELECTOR = 'product-payment-container';
  const STABLE_HOST_SELECTOR = '[data-lensadvisor-stable-host]';
  const PRESERVED_CHILD_SELECTOR = 'button[data-product-add-to-cart-button], .shopify-payment-button, style';

  function ensureStableHost(buyButtonsBlock, paymentContainer) {
    if (!buyButtonsBlock || !paymentContainer) {
      return null;
    }

    let stableHost = buyButtonsBlock.querySelector(STABLE_HOST_SELECTOR);

    if (!stableHost) {
      stableHost = document.createElement('div');
      stableHost.dataset.lensadvisorStableHost = 'true';
      stableHost.className = 'lensadvisor-stable-host';
    }

    if (paymentContainer.nextElementSibling !== stableHost) {
      paymentContainer.insertAdjacentElement('afterend', stableHost);
    }

    return stableHost;
  }

  function moveLensAdvizorNodes(buyButtonsBlock) {
    const paymentContainer = buyButtonsBlock?.querySelector(PAYMENT_CONTAINER_SELECTOR);
    const stableHost = ensureStableHost(buyButtonsBlock, paymentContainer);

    if (!paymentContainer || !stableHost) {
      return;
    }

    Array.from(paymentContainer.children).forEach((child) => {
      if (child.matches(PRESERVED_CHILD_SELECTOR)) {
        return;
      }

      stableHost.appendChild(child);
    });
  }

  function setupLensAdvizorBridge(productForm) {
    if (!productForm || productForm.dataset.lensadvisorBridgeInitialized === 'true') {
      return;
    }

    const buyButtonsBlock = productForm.closest(BUY_BUTTONS_SELECTOR);

    if (!buyButtonsBlock) {
      return;
    }

    productForm.dataset.lensadvisorBridgeInitialized = 'true';

    const syncLensAdvizorNodes = () => {
      moveLensAdvizorNodes(buyButtonsBlock);
    };

    syncLensAdvizorNodes();

    productForm.addEventListener('variant:changed', () => {
      syncLensAdvizorNodes();
      setTimeout(syncLensAdvizorNodes, 150);
      setTimeout(syncLensAdvizorNodes, 400);
    });

    const observer = new MutationObserver(() => {
      syncLensAdvizorNodes();
    });

    observer.observe(buyButtonsBlock, {
      childList: true,
      subtree: true
    });
  }

  function initLensAdvizorBridge() {
    document.querySelectorAll(PRODUCT_FORM_SELECTOR).forEach(setupLensAdvizorBridge);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initLensAdvizorBridge);
  } else {
    initLensAdvizorBridge();
  }

  const formObserver = new MutationObserver(() => {
    initLensAdvizorBridge();
  });

  formObserver.observe(document.body, {
    childList: true,
    subtree: true
  });
})();

// Mirror the Restock Rocket CTA into a stable host below the main product rerender
// while leaving the original app node in place for popup bindings.
(function() {
  const PRODUCT_FORM_SELECTOR = 'form[is="product-form"]';
  const PRODUCT_RERENDER_SELECTOR = 'product-rerender[observe-form]';
  const RESTOCK_BUTTON_SELECTOR = '.restock-rocket-button-container';
  const RESTOCK_BUTTON_INNER_SELECTOR = '.restock-rocket-button-product';
  const RESTOCK_BUTTON_COVER_SELECTOR = '.restock-rocket-button-cover';
  const RESTOCK_APP_BLOCK_SELECTOR = '.shopify-app-block:has(#RestockRocketConfig), .shopify-app-block:has(#RestockRocketStyle)';
  const RESTOCK_PROXY_SELECTOR = '[data-restock-rocket-proxy]';

  function getStableHost(productForm) {
    if (!productForm?.id) {
      return null;
    }

    return document.querySelector(`[data-restock-rocket-stable-host="${CSS.escape(productForm.id)}"]`);
  }

  function findRestockButton(productForm) {
    const productRoot = productForm?.closest('.product-form');

    if (!productRoot) {
      return null;
    }

    const directButton = productRoot.querySelector(RESTOCK_BUTTON_SELECTOR);
    if (directButton) {
      return directButton;
    }

    const appBlock = productRoot.querySelector(RESTOCK_APP_BLOCK_SELECTOR);
    return appBlock?.querySelector(RESTOCK_BUTTON_SELECTOR) || null;
  }

  function ensureProxyButton(productForm, stableHost, restockButton) {
    let proxyButton = stableHost.querySelector(RESTOCK_PROXY_SELECTOR);
    const sourceButton = restockButton.querySelector(RESTOCK_BUTTON_INNER_SELECTOR);

    if (!sourceButton) {
      stableHost.replaceChildren();
      return null;
    }

    if (!proxyButton) {
      proxyButton = document.createElement('button');
      proxyButton.type = 'button';
      proxyButton.dataset.restockRocketProxy = 'true';
      proxyButton.className = sourceButton.className;
      stableHost.replaceChildren(proxyButton);
    } else if (proxyButton.className !== sourceButton.className) {
      proxyButton.className = sourceButton.className;
    }

    restockButton.dataset.restockRocketSource = 'true';
    proxyButton.innerHTML = sourceButton.innerHTML;
    proxyButton.disabled = sourceButton.disabled;
    proxyButton.setAttribute('aria-haspopup', sourceButton.getAttribute('aria-haspopup') || 'dialog');
    proxyButton.onclick = function() {
      const liveRestockButton = findRestockButton(productForm);
      const liveSourceButton = liveRestockButton?.querySelector(RESTOCK_BUTTON_INNER_SELECTOR) || null;
      triggerRestockPopup(liveRestockButton || restockButton, liveSourceButton || sourceButton);
    };

    return proxyButton;
  }

  function dispatchPointerSequence(target) {
    ['pointerdown', 'mousedown', 'pointerup', 'mouseup', 'click'].forEach((eventName) => {
      const event = new MouseEvent(eventName, {
        bubbles: true,
        cancelable: true,
        view: window
      });

      target.dispatchEvent(event);
    });
  }

  function triggerRestockPopup(restockContainer, sourceButton) {
    const sourceCover = restockContainer.querySelector(RESTOCK_BUTTON_COVER_SELECTOR);
    const triggerTarget = sourceCover || sourceButton || restockContainer;

    if (!triggerTarget) {
      return;
    }

    dispatchPointerSequence(triggerTarget);
  }

  function syncRestockButton(productForm) {
    const stableHost = getStableHost(productForm);
    const restockButton = findRestockButton(productForm);

    if (!stableHost || !restockButton) {
      if (stableHost) {
        stableHost.replaceChildren();
      }
      return;
    }

    ensureProxyButton(productForm, stableHost, restockButton);

    stableHost.hidden = false;
  }

  function setupRestockBridge(productForm) {
    if (!productForm || productForm.dataset.restockStableHostInitialized === 'true') {
      return;
    }

    const productRerender = productForm.closest(PRODUCT_RERENDER_SELECTOR);

    if (!productRerender) {
      return;
    }

    productForm.dataset.restockStableHostInitialized = 'true';

    const syncRestockProxy = () => {
      syncRestockButton(productForm);
    };

    syncRestockProxy();

    productForm.addEventListener('variant:changed', () => {
      syncRestockProxy();
      setTimeout(syncRestockProxy, 150);
      setTimeout(syncRestockProxy, 400);
      setTimeout(syncRestockProxy, 800);
    });

    const observer = new MutationObserver(() => {
      syncRestockProxy();
    });

    observer.observe(productRerender, {
      childList: true,
      subtree: true
    });
  }

  function initRestockBridge() {
    document.querySelectorAll(PRODUCT_FORM_SELECTOR).forEach(setupRestockBridge);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initRestockBridge);
  } else {
    initRestockBridge();
  }

  const observer = new MutationObserver(() => {
    initRestockBridge();
  });

  observer.observe(document.body, {
    childList: true,
    subtree: true
  });
})();
